import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'

import { Button, Grid, TextField } from '@mui/material'

import {
    OrganizationPaymentType,
    organizationPaymentSchema,
} from '@/api/organizations'
import {
    organizationPaymentQueryOptions,
    useResetMemberFeeClearedMutation,
    useUpdateOrganizationPaymentMutation,
} from '@/api/organizationsQueryOptions'
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions'
import LoadProgress from '@/components/LoadProgress'
import SelectNewsletterCalculation from '@/components/select/SelectNewsletterCalculation'
import SelectPaymentCalculation from '@/components/select/SelectPaymentCalculation'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { useEffect } from 'react'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/payment',
)({
    component: OrganizationComponent,
})

function OrganizationComponent() {
    const params = Route.useParams()
    const organizationQuery = useSuspenseQuery(
        organizationPaymentQueryOptions(params.organizationUuid),
    )
    const organization = organizationQuery.data

    const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('organization', params.organizationUuid, ['MemberFeeCleared']))
    const permissions = permissionsQuery.data
    const resetMemberFee = permissions && permissions.filter(p => p.detail === 'MemberFeeCleared' && p.permissions.some(a => a === 'write')).length > 0

    const resetMemberFeeClearedMutation = useResetMemberFeeClearedMutation(params.organizationUuid)

    const updateOrganizationPaymentMutation =
        useUpdateOrganizationPaymentMutation()

    const {
        control,
        register,
        handleSubmit,
        watch,
        formState: { isValid, errors },
    } = useForm<OrganizationPaymentType>({
        resolver: yupResolver<OrganizationPaymentType, any, any>(organizationPaymentSchema),
        mode: 'onBlur',
        shouldFocusError: true,
    })

    const onSubmit = (data: OrganizationPaymentType) => {
        if (isValid) {
            updateOrganizationPaymentMutation.mutate({ ...organization, ...data })
            enqueueSnackbar(t('action.changed', { entity: t('entity.organization') }), {
                preventDuplicate: true,
                variant: 'success',
            })
        } else {
            console.log(errors)
        }
    }

    const onError = () => {
        console.log('error')
    }

    useEffect(() => {
        const subscription = watch((_value, { name }) => {
            if (name) {
                handleSubmit(onSubmit, onError)()
            }
        })

        return () => subscription.unsubscribe()
    })

    if (
        organizationQuery.isLoading ||
        organizationQuery.isFetching ||
        !organization
    ) {
        return <LoadProgress />
    }

    const writeable = organization.permissions
        ? organization.permissions.some((perm) => perm === 'write')
        : false

    return (
        (<>
            <form>
                <Grid container spacing={1}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 8
                        }}>
                        <div>
                            <Grid container spacing={1}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 3
                                    }}>
                                    <input
                                        type="hidden"
                                        {...register('uuid')}
                                        defaultValue={organization.uuid}
                                    />
                                    <TextField
                                        defaultValue={organization.memberFeeFull}
                                        {...register('memberFeeFull')}
                                        disabled={!writeable}
                                        label={t('property.organization.memberFeeFull')}
                                        helperText={errors.memberFeeFull?.message}
                                    />
                                    <TextField
                                        defaultValue={organization.memberFeeReduced}
                                        {...register('memberFeeReduced')}
                                        disabled={!writeable}
                                        label={t('property.organization.memberFeeReduced')}
                                        helperText={errors.memberFeeReduced?.message}
                                    />
                                    <TextField
                                        defaultValue={organization.memberFeeReduced2}
                                        {...register('memberFeeReduced2')}
                                        disabled={!writeable}
                                        label={t('property.organization.memberFeeReduced2')}
                                        helperText={errors.memberFeeReduced2?.message}
                                    />
                                    <TextField
                                        defaultValue={organization.memberFeePwa}
                                        {...register('memberFeePwa')}
                                        disabled={!writeable}
                                        label={t('property.organization.memberFeePwa')}
                                        helperText={errors.memberFeePwa?.message}
                                    />
                                    <TextField
                                        defaultValue={organization.memberFeeIndividual1}
                                        {...register('memberFeeIndividual1')}
                                        disabled={!writeable}
                                        label={t('property.organization.memberFeeIndividual1')}
                                        helperText={errors.memberFeeIndividual1?.message}
                                    />
                                    <TextField
                                        defaultValue={organization.memberFeeIndividual2}
                                        {...register('memberFeeIndividual2')}
                                        disabled={!writeable}
                                        label={t('property.organization.memberFeeIndividual2')}
                                        helperText={errors.memberFeeIndividual2?.message}
                                    />
                                    <TextField
                                        defaultValue={organization.memberFeeIndividual3}
                                        {...register('memberFeeIndividual3')}
                                        disabled={!writeable}
                                        label={t('property.organization.memberFeeIndividual3')}
                                        helperText={errors.memberFeeIndividual3?.message}
                                    />
                                    <TextField
                                        defaultValue={organization.newsletterFee}
                                        {...register('newsletterFee')}
                                        disabled={!writeable}
                                        label={t('property.organization.newsletterFee')}
                                        helperText={errors.newsletterFee?.message}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 6
                                    }}>
                                    <SelectPaymentCalculation
                                        control={control}
                                        name="paymentCalculation.uuid"
                                        orgUuid={organization.uuid}
                                        payment={organization.paymentCalculation}
                                        disabled={!writeable}
                                    />
                                    <SelectNewsletterCalculation
                                        control={control}
                                        name="newsletterCalculation.uuid"
                                        orgUuid={organization.uuid}
                                        calculation={organization.newsletterCalculation}
                                        disabled={!writeable}
                                        label={t('property.organization.newsletterCalculation')}
                                    />
                                    <SelectNewsletterCalculation
                                        control={control}
                                        name="mailingCalculation.uuid"
                                        orgUuid={organization.uuid}
                                        calculation={organization.mailingCalculation}
                                        disabled={!writeable}
                                        label={t('property.organization.mailingCalculation')}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>
                </Grid>
            </form>
            {resetMemberFee && (
                <Grid container spacing={1}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 8
                        }}>
                        <div>
                            <Grid container spacing={1}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 3
                                    }}>

                                    <Button variant='outlined' onClick={() => resetMemberFeeClearedMutation.mutate()} >
                                        {t('action.resetMemberFeeCleared')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>
                </Grid>
            )
            }
        </>)
    );
}
